import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Github, Linkedin, FileText } from "lucide-react";
import { profile, publishedProjects, workflowPillars } from "../data/content";
import { MaskedLines, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProjectCard from "../components/ProjectCard";
import SkillSphere from "../components/SkillSphere";

const featuredSlugs = [
    "retail-demand-forecasting",
    "credit-risk-pd-model",
    "retail-allocation-simulator",
];

export default function Home() {
    const featuredProjects = featuredSlugs
        .map((slug) => publishedProjects.find((project) => project.slug === slug))
        .filter(Boolean);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative flex min-h-[88svh] items-center overflow-hidden px-6 pb-12 pt-20 lg:px-12">
                <SkillSphere className="pointer-events-none absolute inset-0 h-full w-full opacity-20 md:opacity-70 lg:opacity-100" />
                <div className="site-shell relative z-10 w-full">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.6 }}
                        className="mb-8 font-mono text-sm uppercase tracking-wider text-teal"
                        data-testid="hero-eyebrow"
                    >
                        {profile.roleShort}
                    </motion.p>

                    <h1
                        className="font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.035em] text-navy sm:text-5xl md:text-6xl md:leading-[0.95]"
                        data-testid="hero-headline"
                    >
                        <MaskedLines lines={["In a world full of answers,"]} />
                        <span className="block overflow-hidden">
                            <motion.span
                                className="block text-teal"
                                initial={{ y: "110%" }}
                                animate={{ y: "0%" }}
                                transition={{ duration: 0.9, delay: 0.39, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <span className="block">the right solution begins{" "}</span>
                                <span className="block">with the right question.</span>
                            </motion.span>
                        </span>
                    </h1>

                    <div className="mt-10 grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-end">
                        <Reveal delay={0.5}>
                            <p className="max-w-xl text-base leading-relaxed text-navy/70 md:text-lg">
                                {profile.heroIntro}
                            </p>
                            <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/70 md:text-lg">
                                {profile.heroSupport}
                            </p>
                        </Reveal>

                        <Reveal delay={0.6} className="flex flex-wrap gap-3 md:justify-end">
                            <Link
                                to="/ask"
                                data-testid="hero-ask-portfolio"
                                className="group flex items-center gap-2 border border-teal bg-teal/15 px-6 py-3 font-mono text-xs uppercase tracking-widest text-teal transition-colors hover:bg-teal hover:text-cream"
                            >
                                Ask the portfolio
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/projects"
                                data-testid="hero-view-projects"
                                className="group flex items-center gap-2 border border-navy bg-navy px-6 py-3 font-mono text-xs uppercase tracking-widest text-cream transition-colors hover:bg-teal hover:border-teal"
                            >
                                View Portfolio
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/resume"
                                data-testid="hero-download-resume"
                                className="flex items-center gap-2 border border-navy px-6 py-3 font-mono text-xs uppercase tracking-widest text-navy transition-colors hover:bg-navy hover:text-cream"
                            >
                                <FileText size={16} /> Resume
                            </Link>
                            <a
                                href={profile.github}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-github"
                                className="flex items-center border border-navy/20 px-4 py-3 text-navy transition-colors hover:bg-navy hover:text-cream"
                                aria-label="GitHub"
                            >
                                <Github size={16} />
                            </a>
                            <a
                                href={profile.linkedin}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-linkedin"
                                className="flex items-center border border-navy/20 px-4 py-3 text-navy transition-colors hover:bg-navy hover:text-cream"
                                aria-label="LinkedIn"
                            >
                                <Linkedin size={16} />
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Marquee />

            <section className="px-6 py-14 lg:px-12" data-testid="home-ask">
                <div className="site-shell">
                    <Reveal className="grid gap-8 border border-navy/15 bg-surface/40 p-6 md:grid-cols-[1.1fr_0.9fr] md:items-center md:p-8">
                        <div>
                            <p className="font-mono text-sm uppercase tracking-wider text-teal">Discovery</p>
                            <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold tracking-tight text-navy md:text-4xl">
                                Ask questions. Get linked to the right work.
                            </h2>
                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy/65 md:text-base">
                                A grounded portfolio assistant that synthesizes public evidence across experience,
                                projects, and methods — then points you to the pages worth opening next.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 md:items-end">
                            <Link
                                to="/ask"
                                data-testid="home-ask-cta"
                                className="inline-flex items-center gap-2 border border-navy bg-navy px-6 py-3 font-mono text-xs uppercase tracking-widest text-cream transition-colors hover:border-teal hover:bg-teal"
                            >
                                Open portfolio assistant
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                to="/projects/interactive-rag"
                                className="font-mono text-[11px] uppercase tracking-widest text-navy/45 hover:text-teal"
                            >
                                Technical RAG case study →
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="px-6 py-14 lg:px-12" data-testid="home-workflow">
                <div className="site-shell">
                    <Reveal>
                        <p className="font-mono text-sm uppercase tracking-wider text-teal">How I work</p>
                        <h2 className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-navy md:text-4xl">
                            Analysis first. AI used to extend what the work can become.
                        </h2>
                    </Reveal>
                    <div className="mt-8 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
                        {workflowPillars.map((pillar, index) => (
                            <Reveal key={pillar.title} delay={index * 0.06}>
                                <article className="h-full bg-surface p-6">
                                    <span className="font-mono text-xs text-teal">{String(index + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-3 font-display text-xl font-bold text-navy">{pillar.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-navy/65">{pillar.description}</p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SELECTED WORK */}
            <section className="px-6 py-16 lg:px-12 lg:py-20" data-testid="home-projects">
                <div className="site-shell">
                    <Reveal className="mb-8 flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <p className="font-mono text-sm uppercase tracking-wider text-teal">
                                Portfolio
                            </p>
                            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy md:text-4xl">
                                Selected case studies
                            </h2>
                        </div>
                        <Link
                            to="/projects"
                            data-testid="home-all-projects"
                            className="nav-underline font-mono text-sm uppercase tracking-wider text-navy"
                        >
                            View all case studies →
                        </Link>
                    </Reveal>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                        {featuredProjects.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 0.08}>
                                <ProjectCard project={p} index={i} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="px-6 pb-20 lg:px-12" data-testid="home-cta">
                <Reveal className="site-shell border border-navy/10 bg-surface px-8 py-16 text-navy md:px-16">
                    <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                        <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Interested in analytics, forecasting, or modeling? Let’s connect.
                        </h2>
                        <Link
                            to="/resume"
                            data-testid="home-cta-contact"
                            className="flex shrink-0 items-center gap-2 border border-teal bg-teal px-6 py-3 font-mono text-xs uppercase tracking-widest text-cream transition-colors hover:bg-teal-hover hover:border-teal-hover"
                        >
                            Get in touch <ArrowRight size={16} />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
